import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuthStore } from "@/store/use-auth-store";
import { useCompleteProfile } from "@/hooks";
import { Button } from "@/components/ui/button";
import { StyledInput } from "@/components/ui/styled-input";
import { StyledLabel } from "@/components/ui/styled-label";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required").min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(1, "Last name is required").min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(1, "Phone number is required").min(8, "Please enter a valid phone number"),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface CompleteProfileStepProps {
  onComplete: () => void;
}

export const CompleteProfileStep = ({ onComplete }: CompleteProfileStepProps) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const completeProfileMutation = useCompleteProfile();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
    },
  });

  const phoneValue = watch("phone");

  useEffect(() => {
    if (user) {
      setValue("firstName", user.firstName || "");
      setValue("lastName", user.lastName || "");
      setValue("email", user.email || "");
      setValue("phone", user.phone || "");
    }
  }, [user, setValue]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      if (!user?.id) {
        toast.error(t("onboarding.userNotFound"));
        return;
      }
      
      // Await mutation and verify it succeeded
      const result = await completeProfileMutation.mutateAsync({
        userId: user.id,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
      });
      
      // Verify the mutation succeeded before proceeding
      // Only call onComplete if mutation completed successfully
      if (result) {
        onComplete();
      }
    } catch (error) {
      // Error is handled by the hook, but we don't call onComplete()
      // This ensures we stay on the current step if profile update fails
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-2">{t("onboarding.steps.profile.title")}</h2>
      <p className="text-gray-600 mb-6">{t("onboarding.steps.profile.description")}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <StyledLabel htmlFor="firstName">
              {t("onboarding.steps.profile.firstName")}
            </StyledLabel>
            <StyledInput
              id="firstName"
              type="text"
              placeholder={t("onboarding.steps.profile.firstName")}
              error={errors.firstName?.message}
              {...register("firstName")}
            />
          </div>

          <div>
            <StyledLabel htmlFor="lastName">
              {t("onboarding.steps.profile.lastName")}
            </StyledLabel>
            <StyledInput
              id="lastName"
              type="text"
              placeholder={t("onboarding.steps.profile.lastName")}
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </div>
        </div>

        <div>
          <StyledLabel htmlFor="email">
            {t("onboarding.steps.profile.email")}
          </StyledLabel>
          <StyledInput
            id="email"
            type="email"
            placeholder={t("onboarding.steps.profile.email")}
            disabled
            {...register("email")}
          />
          <p className="text-sm text-gray-500 mt-1">{t("onboarding.steps.profile.emailReadOnly")}</p>
        </div>

        <div>
          <StyledLabel htmlFor="phone" required>
            {t("onboarding.steps.profile.phone")}
          </StyledLabel>
          <div className="mt-1">
            <PhoneInput
              country={'rw'}
              value={phoneValue || ""}
              onChange={(value) => {
                setValue("phone", value, { shouldValidate: true });
              }}
              inputProps={{
                required: true,
                autoFocus: false,
                id: "phone",
              }}
              containerClass={errors.phone ? "phone-input-error" : ""}
              inputClass={errors.phone ? "border-red-500" : ""}
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1 pl-2">{errors.phone.message}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">{t("onboarding.steps.profile.phoneHint")}</p>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            type="submit"
            disabled={completeProfileMutation.isPending}
          >
            {completeProfileMutation.isPending
              ? t("common.saving")
              : t("onboarding.steps.profile.continue")}
          </Button>
        </div>
      </form>
    </div>
  );
};

